export function diff<T extends Record<string, a>>(
  old: T | undefined,
  new_: T | undefined,
): Partial<T> {
  if (old === new_) return {}

  const isNil = (v: a) => v === undefined || v === null

  if (isNil(old) && !isNil(new_)) return new_ as Partial<T>
  if (isNil(new_) && !isNil(old)) return old as Partial<T>

  if (isNil(old) || isNil(new_)) return {} // eg old null, new undefined

  const patch: a = {}

  const keys = new Set([...Object.keys(old as object), ...Object.keys(new_ as object)])

  for (const key of keys) {
    const oldV = (old as Record<string, a>)[key]
    const newV = (new_ as Record<string, a>)[key]

    if (oldV === newV) continue

    if (oldV instanceof Date || newV instanceof Date) {
      const oldTime = oldV instanceof Date ? oldV.getTime() : undefined // one of the values can be not a date and not nullish
      const newTime = newV instanceof Date ? newV.getTime() : undefined
      if (oldTime !== newTime) patch[key] = newV
      continue
    }

    if (typeof oldV !== 'object' && typeof newV !== 'object') {
      if (oldV !== newV) patch[key] = newV
      continue
    }

    // Explicitly handle null transitions
    if (oldV === null && newV !== null) {
      patch[key] = newV
      continue
    }

    if (oldV !== null && newV === null) {
      patch[key] = null
      continue
    }

    if (oldV === undefined && newV !== undefined) {
      patch[key] = newV
      continue
    }

    if (oldV !== undefined && newV === undefined) {
      patch[key] = undefined
      continue
    }

    // Handle type mismatch between array and non-array objects
    if (Array.isArray(oldV) !== Array.isArray(newV)) {
      patch[key] = newV
      continue
    }

    if (Array.isArray(oldV) && Array.isArray(newV)) {
      const isObjectWithId = (item: a) =>
        item !== null &&
        typeof item === 'object' &&
        'id' in item &&
        item.id !== undefined &&
        item.id !== null

      const bothHaveIds = oldV.every(isObjectWithId) && newV.every(isObjectWithId)

      if (!bothHaveIds) {
        // Fallback: treat arrays without ids (or with null items) as full replacement
        // Only set patch if arrays differ (shallow equality by index)
        const sameLength = oldV.length === newV.length
        const sameItems = sameLength && oldV.every((v, i) => v === newV[i])
        if (!sameItems) patch[key] = newV
        continue
      }

      const added: a[] = []
      const deleted: { id: string; _i_: -1 }[] = []
      const updated: a[] = []

      const oldMap = new Map(oldV.map((item) => [item.id, item]))
      const newMap = new Map(newV.map((item) => [item.id, item]))

      for (const oldItem of oldV) {
        if (!newMap.has(oldItem.id)) {
          deleted.push({ id: oldItem.id, _i_: -1 })
        }
      }

      for (const newItem of newV) {
        const oldItem = oldMap.get(newItem.id)

        if (!oldItem) {
          added.push(newItem)
        } else {
          const difference = diff(oldItem, newItem)
          if (Object.keys(difference).length > 0) {
            const updatedItem = { id: newItem.id, ...difference }
            updated.push(updatedItem)
          }
        }
      }

      if (added.length || deleted.length || updated.length) {
        patch[key] = [...deleted, ...added, ...updated]
      }

      continue
    }

    if (typeof oldV === 'object' && oldV !== null && typeof newV === 'object' && newV !== null) {
      const nestedDiff = diff(oldV, newV)
      if (Object.keys(nestedDiff).length > 0) {
        patch[key] = nestedDiff
      }
      continue
    }

    // If we reach here, we have a type mismatch (e.g., array to primitive, object to primitive, etc.)
    // Just replace with the new value
    patch[key] = newV
  }

  return patch
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
type a = any
